import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {Box, Typography, Tabs, Tab, Paper, CircularProgress, Alert, Collapse, Button, IconButton, Chip} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TimelineIcon from "@mui/icons-material/Timeline";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ScienceIcon from "@mui/icons-material/Science";
import OpacityIcon from "@mui/icons-material/Opacity";
import HealingIcon from "@mui/icons-material/Healing";
import WarningIcon from "@mui/icons-material/WarningAmber";
import BiotechIcon from "@mui/icons-material/Biotech";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {getPondSummary, clearPondCache} from "../../api/pond";
import {SurvivalPredictionResponse} from "../../api/ai";
import CycleTab from "./CycleTab";
import FeedTab from "./FeedTab";
import SamplingTab from "./SamplingTab";
import MeasurementTab from "./MeasurementTab";
import TreatmentTab from "./TreatmentTab";
import MortalityTab from "./MortalityTab";
import DiseaseTab from "./DiseaseTab";
import FeedingTrayTab from "./FeedingTrayTab";
import HarvestTab from "./HarvestTab";
import ShrimpPerformanceCalculator from "../../components/common/ShrimpPerformanceCalculator";
import PondPerformanceAnalytics from "../../components/common/PondPerformanceAnalytics";
import EnhancedPondAnalytics from "../../components/common/EnhancedFCRAnalysis";
import {useRef} from "react";
import {formatSurvivalRange, formatSurvivalRateRange, formatBiomassRange, formatAIPredictedCount} from "../../utils/numberFormat";
import {useTranslation} from "react-i18next";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`pond-tabpanel-${index}`} aria-labelledby={`pond-tab-${index}`} {...other}>
      {value === index && (
        <Box
          sx={{
            p: 2,
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
                transform: "translateY(10px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PondDetailPage: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const {t, i18n} = useTranslation();
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);
  const [predictedSurvival, setPredictedSurvival] = useState<SurvivalPredictionResponse | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const summaryData = await getPondSummary(id!);
        console.log("Pond summary data received:", summaryData); // Debug log
        setSummary(summaryData);

        // Debug: Log the full summary data structure
        console.log("Full summary data:", summaryData);
        console.log("Performance metrics:", summaryData?.performanceMetrics);
        console.log("AI analysis in performance metrics:", summaryData?.performanceMetrics?.aiAnalysis);
        console.log("Performance metrics error:", summaryData?.performanceMetrics?.error);
        console.log("Current cycle:", summaryData?.currentCycle);

        // Use AI analysis from performance metrics (already cached)
        if (summaryData?.performanceMetrics?.aiAnalysis) {
          console.log("Using AI analysis from performance metrics:", summaryData.performanceMetrics.aiAnalysis);
          setAiAnalysis(summaryData.performanceMetrics.aiAnalysis);

          // Set predicted survival from the same AI analysis
          if (summaryData.performanceMetrics.aiAnalysis.predictedSurvival !== undefined) {
            setPredictedSurvival(summaryData.performanceMetrics.aiAnalysis);
          }
        } else if (summaryData?.aiAnalysis) {
          // Fallback: check for AI analysis at top level
          console.log("Using AI analysis from top level:", summaryData.aiAnalysis);
          setAiAnalysis(summaryData.aiAnalysis);

          // Set predicted survival from the same AI analysis
          if (summaryData.aiAnalysis.predictedSurvival !== undefined) {
            setPredictedSurvival(summaryData.aiAnalysis);
          }
        } else {
          console.log("No AI analysis available in performance metrics or top level");
          console.log("Available keys in performanceMetrics:", Object.keys(summaryData?.performanceMetrics || {}));
          console.log("Available keys in summaryData:", Object.keys(summaryData || {}));
          setAiAnalysis(null);
          setPredictedSurvival(null);
        }
      } catch (error) {
        console.error("Error fetching pond summary:", error);
        setError("Failed to load pond summary");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSummary();
    }
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!summary) {
    return <Alert severity="warning">No pond data available</Alert>;
  }

  // Safely destructure with fallbacks
  const pond = summary.pond || {};
  const performanceMetrics = summary.performanceMetrics || {};
  
  // Debug: Log the performance metrics data
  console.log("Performance Metrics Debug:", {
    performanceMetrics,
    totalFeed: performanceMetrics.totalFeed,
    abw: performanceMetrics.abw,
    fcr: performanceMetrics.fcr,
    biomassRange: performanceMetrics.biomassRange,
    age: performanceMetrics.age
  });

  // Check if we have the essential pond data
  if (!pond.id || !pond.name) {
    return <Alert severity="error">Invalid pond data received</Alert>;
  }

  return (
    <Box p={{xs: 2, sm: 3}} sx={{overflowX: "auto", width: "100%", maxWidth: "100vw"}}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" gutterBottom>
          {pond.name}
        </Typography>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={async () => {
            try {
              await clearPondCache(id!);
              // Refresh the data
              const summaryData = await getPondSummary(id!);
              setSummary(summaryData);
              if (summaryData?.performanceMetrics?.aiAnalysis) {
                setAiAnalysis(summaryData.performanceMetrics.aiAnalysis);
                if (summaryData.performanceMetrics.aiAnalysis.predictedSurvival !== undefined) {
                  setPredictedSurvival(summaryData.performanceMetrics.aiAnalysis);
                }
              } else if (summaryData?.aiAnalysis) {
                setAiAnalysis(summaryData.aiAnalysis);
                if (summaryData.aiAnalysis.predictedSurvival !== undefined) {
                  setPredictedSurvival(summaryData.aiAnalysis);
                }
              } else {
                setAiAnalysis(null);
                setPredictedSurvival(null);
              }
            } catch (error) {
              console.error('Error clearing cache:', error);
            }
          }}
        >
          Refresh Data
        </Button>
      </Box>
      <Box display="flex" gap={0.5} mb={1} flexWrap="wrap" alignItems="center" sx={{overflowX: "auto", minWidth: 0}}>
        {/* AGE */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("AGE")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {performanceMetrics.age || 0}
          </Typography>
        </Paper>

        {/* STOCKING */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("STOCKING")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {summary.currentCycle?.totalSeed ? summary.currentCycle.totalSeed.toLocaleString() : "-"}
          </Typography>
        </Paper>

        {/* AI PREDICTED SURVIVAL */}
        <Paper
          sx={{
            p: {xs: 0.5, md: 0.5, lg: 1},
            minWidth: {xs: 80, md: 80, lg: 100},
            flex: 1.2,
            textAlign: "center",
            background: "#f6fff6",
          }}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.4rem", md: "0.4rem", lg: "0.5rem"}} color="success.main">
            {t("AI PREDICTED")}
          </Typography>
          <Typography display={"block"} variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}} color="success.main">
            {t("SURVIVAL")}
          </Typography>
          {(() => {
            console.log("AI PREDICTED SURVIVAL Debug:", {
              predictedSurvival,
              hasPredictedSurvival: !!predictedSurvival,
              predictedSurvivalValue: predictedSurvival?.predictedSurvival,
              isNumber: typeof predictedSurvival?.predictedSurvival === "number",
              isValid: predictedSurvival?.predictedSurvival !== null && predictedSurvival?.predictedSurvival !== undefined,
            });

            return predictedSurvival ? (
              <>
                <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}} color="success.main">
                  {predictedSurvival.predictedSurvival !== null && predictedSurvival.predictedSurvival !== undefined
                    ? `${Number(predictedSurvival.predictedSurvival).toFixed(2)}%`
                    : "-"}
                </Typography>
                <Typography variant="body2" fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}} color="success.main">
                  {predictedSurvival.predictedSurvival !== null && predictedSurvival.predictedSurvival !== undefined
                    ? formatAIPredictedCount(Number(predictedSurvival.predictedSurvival), summary.currentCycle?.totalSeed || 0, i18n.language)
                    : "-"}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}} color="text.secondary">
                  {t("N/A")}
                </Typography>
                <Typography variant="body2" fontSize={{xs: "0.5rem", md: "0.5rem", lg: "0.6rem"}} color="text.secondary">
                  {t("AI not configured")}
                </Typography>
              </>
            );
          })()}
        </Paper>

        {/* BIOMASS */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 80, md: 80, lg: 100}, flex: 1.5, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("BIOMASS")}
          </Typography>
          <br />
          <Typography
            variant="body2"
            fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}
            sx={{
              whiteSpace: "nowrap",
              lineHeight: 1.2,
            }}>
            {formatBiomassRange(performanceMetrics.biomassRange)}
          </Typography>
        </Paper>

        {/* FEED */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("FEED")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {performanceMetrics.totalFeed ? Math.round(Number(performanceMetrics.totalFeed)) : "-"} kg
          </Typography>
        </Paper>

        {/* ABW */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("ABW")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {performanceMetrics.abw ? Number(performanceMetrics.abw).toFixed(2) : "-"}
          </Typography>
        </Paper>

        {/* FCR */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("FCR")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {performanceMetrics.fcr ? Number(performanceMetrics.fcr).toFixed(2) : "-"}
          </Typography>
          {performanceMetrics.templateFCR && (
            <Typography variant="caption" fontSize={{xs: "0.5rem", md: "0.5rem", lg: "0.6rem"}} color="text.secondary">
              Template: {Number(performanceMetrics.templateFCR).toFixed(2)}
            </Typography>
          )}
        </Paper>
      </Box>
      {/* AI Recommendations Section */}
      {(() => {
        // Check for AI data from dedicated AI analysis endpoint
        const aiData = aiAnalysis || predictedSurvival;
        const hasAiData = aiData && (aiAnalysis?.enabled || predictedSurvival);

        console.log("AI Analysis Debug:", {
          hasAiAnalysis: !!aiAnalysis,
          hasPredictedSurvival: !!predictedSurvival,
          aiAnalysis: aiAnalysis,
          predictedSurvival: predictedSurvival,
          aiData: aiData,
          hasAiData: hasAiData,
          isEnabled: aiAnalysis?.enabled,
          hasAnalysis: !!aiData?.analysis,
          hasRecommendations: !!aiData?.recommendations?.length,
          hasInsights: !!aiData?.insights?.length,
        });

        if (hasAiData && aiData.analysis) {
          return (
            <Box my={3}>
              <Paper sx={{p: 3, backgroundColor: "#f8f9fa", border: "2px solid #e3f2fd"}}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" color="primary" fontWeight={600}>
                    {`🤖 ${t("AI Recommendations")}`}
                  </Typography>
                  <IconButton onClick={() => setShowAIRecommendations(!showAIRecommendations)} size="small" sx={{color: "primary.main"}}>
                    {showAIRecommendations ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={showAIRecommendations}>
                  {/* AI Recommendations */}
                  {aiData.recommendations && aiData.recommendations.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom color="success.main">
                        🎯 Actionable Recommendations
                      </Typography>
                      <Box component="ul" sx={{pl: 2, m: 0}}>
                        {aiData.recommendations.map((recommendation: string, index: number) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              lineHeight: 1.5,
                              "&::marker": {color: "success.main"},
                            }}>
                            {recommendation}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* AI Insights */}
                  {aiData.insights && aiData.insights.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom color="info.main">
                        💡 Key Insights
                      </Typography>
                      <Box component="ul" sx={{pl: 2, m: 0}}>
                        {aiData.insights.map((insight: string, index: number) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              lineHeight: 1.5,
                              "&::marker": {color: "info.main"},
                            }}>
                            {insight}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Collapse>
              </Paper>
            </Box>
          );
        } else {
          // Show a more helpful message when AI analysis is not available
          return (
            <Box my={3}>
              <Paper sx={{p: 3, backgroundColor: "#fff3e0", border: "2px solid #ff9800"}}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h6" color="warning.main" fontWeight={600}>
                    🤖 AI Recommendations
                  </Typography>
                  <Chip label="Info: AI-001" color="warning" size="small" variant="outlined" />
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{mt: 1}}>
                  AI recommendations are not available at this time. This could be due to:
                </Typography>
                <Box component="ul" sx={{pl: 2, mt: 1, mb: 0}}>
                  <Typography component="li" variant="body2" color="textSecondary">
                    • No AI services configured (Gemini, OpenAI, or Ollama)
                  </Typography>
                  <Typography component="li" variant="body2" color="textSecondary">
                    • Insufficient data for AI analysis
                  </Typography>
                  <Typography component="li" variant="body2" color="textSecondary">
                    • AI services temporarily unavailable
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{mt: 1, fontStyle: "italic"}}>
                  Performance metrics are still available and accurate.
                </Typography>
              </Paper>
            </Box>
          );
        }
      })()}

      {/* Shrimp Performance Calculator Section */}
      <Box my={3}>
        <Button variant="outlined" color="primary" onClick={() => setShowCalculator((prev) => !prev)} sx={{mb: 1}}>
          {showCalculator ? t("Hide Performance Calculator") : t("Show Performance Calculator")}
        </Button>
        <Collapse in={showCalculator}>
          <Typography variant="h6" gutterBottom color="primary">
            Performance Calculator
          </Typography>
          <ShrimpPerformanceCalculator
            pondData={{
              currentCycle: summary.currentCycle,
              performanceMetrics: summary.performanceMetrics,
            }}
          />
        </Collapse>
      </Box>
      {/* Professional Tab menu wrapper with 1024px constraint */}
      <Box
        position="relative"
        sx={{
          maxWidth: "1024px",
          overflowX: "auto",
          whiteSpace: "nowrap",
          mb: 3,
          backgroundColor: "#ffffff",
          borderRadius: 2,
          p: 1,
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          "&::-webkit-scrollbar": {height: 6},
          "&::-webkit-scrollbar-track": {backgroundColor: "#f5f5f5", borderRadius: 3},
          "&::-webkit-scrollbar-thumb": {backgroundColor: "#c1c1c1", borderRadius: 3, "&:hover": {backgroundColor: "#a8a8a8"}},
        }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minWidth: 0,
            ".MuiTabs-flexContainer": {flexWrap: "nowrap", gap: 0.5},
            ".MuiTab-root": {
              minWidth: "auto",
              minHeight: "40px",
              padding: "8px 12px",
              fontSize: {xs: "0.7rem", sm: "0.75rem", md: "0.8rem"},
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "6px",
              margin: "0 2px",
              transition: "all 0.2s ease",
              "&.Mui-selected": {
                backgroundColor: "#388e3c",
                color: "#ffffff",
                boxShadow: "0 2px 4px rgba(56,142,60,0.3)",
                transform: "translateY(-1px)",
              },
              "&:hover": {backgroundColor: "#f5f5f5", "&.Mui-selected": {backgroundColor: "#2e7d32"}},
            },
            ".MuiTabs-scrollButtons": {width: "24px", height: "24px", "&.Mui-disabled": {opacity: 0.3}},
            ".MuiTabs-indicator": {display: "none"},
          }}>
          <Tab label={t("Cycle")} />
          <Tab label={t("Feed")} />
          <Tab label={t("Sampling")} />
          <Tab label={t("Mortality")} />
          <Tab label={t("Water Quality")} />
          <Tab label={t("Disease")} />
          <Tab label={t("Feeding Tray")} />
          <Tab label={t("Treatment")} />
          <Tab label={t("Harvest")} />
          <Tab label={t("Analytics")} />
        </Tabs>
      </Box>
      <TabPanel value={tab} index={0}>
        <CycleTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <FeedTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <SamplingTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <MortalityTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <MeasurementTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={5}>
        <DiseaseTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={6}>
        <FeedingTrayTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={7}>
        <TreatmentTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={8}>
        <HarvestTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={9}>
        <Box>
          <EnhancedPondAnalytics
            aiAnalysis={performanceMetrics.aiAnalysis}
            performanceMetrics={performanceMetrics}
            loading={loading}
          />
          <Box mt={4}>
            <PondPerformanceAnalytics pondId={id!} />
          </Box>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default PondDetailPage;
